import React from "react";
import { Animated, Easing, View } from "react-native";

const DEFAULT_SLIDE_MS = 520;
const DEFAULT_FADE_DELAY_MS = 300;
const DEFAULT_FADE_MS = 420;
const DEFAULT_IO_ROOT_MARGIN = "72px 0px";
const DEFAULT_IO_THRESHOLD = 0.08;
/** If ref never becomes a DOM node, play once anyway (avoids stuck hidden state). */
const ATTACH_DOM_MAX_FRAMES = 240;

export type UseViewportSlideFadeIntroOptions = {
  slideDistancePx: number;
  slideMs?: number;
  fadeDelayMs?: number;
  fadeMs?: number;
  rootMargin?: string;
  threshold?: number;
};

function toDomNode(node: View | null): Element | null {
  if (
    node == null ||
    typeof Element === "undefined" ||
    !((node as unknown) instanceof Element)
  ) {
    return null;
  }
  return node as unknown as Element;
}

/**
 * Web (react-native-web): slide-up + delayed fade when `shellRef` enters the viewport.
 * Intended for web-only UI (e.g. course footer). On runtimes without `IntersectionObserver`,
 * shows the final state with no animation.
 */
export function useViewportSlideFadeIntro({
  slideDistancePx,
  slideMs = DEFAULT_SLIDE_MS,
  fadeDelayMs = DEFAULT_FADE_DELAY_MS,
  fadeMs = DEFAULT_FADE_MS,
  rootMargin = DEFAULT_IO_ROOT_MARGIN,
  threshold = DEFAULT_IO_THRESHOLD,
}: UseViewportSlideFadeIntroOptions) {
  const shellRef = React.useRef<View>(null);
  const playedRef = React.useRef(false);

  const translateY = React.useRef(new Animated.Value(0)).current;
  const overlayOpacity = React.useRef(new Animated.Value(0)).current;

  React.useLayoutEffect(() => {
    if (playedRef.current) {
      translateY.setValue(0);
      overlayOpacity.setValue(1);
      return;
    }
    translateY.setValue(slideDistancePx);
    overlayOpacity.setValue(0);
  }, [slideDistancePx, translateY, overlayOpacity]);

  React.useEffect(() => {
    if (playedRef.current) return;

    if (typeof IntersectionObserver === "undefined") {
      playedRef.current = true;
      translateY.setValue(0);
      overlayOpacity.setValue(1);
      return;
    }

    let cancelled = false;
    let io: IntersectionObserver | null = null;
    let rafId = 0;
    let attachFrames = 0;

    const run = () => {
      if (cancelled || playedRef.current) return;
      playedRef.current = true;

      const reduceMotion =
        typeof window !== "undefined" &&
        (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ??
          false);

      if (reduceMotion) {
        translateY.setValue(0);
        overlayOpacity.setValue(1);
        return;
      }

      translateY.setValue(slideDistancePx);
      overlayOpacity.setValue(0);

      Animated.parallel([
        Animated.timing(translateY, {
          toValue: 0,
          duration: slideMs,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: false,
        }),
        Animated.sequence([
          Animated.delay(fadeDelayMs),
          Animated.timing(overlayOpacity, {
            toValue: 1,
            duration: fadeMs,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
          }),
        ]),
      ]).start();
    };

    const attachWhenReady = () => {
      if (cancelled || playedRef.current) return;
      const dom = toDomNode(shellRef.current);
      if (dom) {
        io = new IntersectionObserver(
          (entries) => {
            if (!entries[0]?.isIntersecting) return;
            io?.disconnect();
            io = null;
            run();
          },
          { threshold, rootMargin },
        );
        io.observe(dom);
        return;
      }
      if (++attachFrames >= ATTACH_DOM_MAX_FRAMES) {
        requestAnimationFrame(run);
        return;
      }
      rafId = requestAnimationFrame(attachWhenReady);
    };

    attachWhenReady();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      io?.disconnect();
    };
  }, [
    slideDistancePx,
    slideMs,
    fadeDelayMs,
    fadeMs,
    rootMargin,
    threshold,
    translateY,
    overlayOpacity,
  ]);

  return { shellRef, translateY, overlayOpacity };
}
